import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';

// Wire types + calls for the dynamic dashboard (server: modules/dashboard). The server is the authority on every one of
// these: it decides which widgets exist for this user, validates every layout, and re-checks permissions on each load.

export type RangeKey = 'today' | 'yesterday' | 'last7' | 'mtd' | 'last30' | 'lastMonth';
export const RANGE_LABELS: Record<RangeKey, string> = {
  today: 'Today', yesterday: 'Yesterday', last7: 'Last 7 days', mtd: 'Month to date', last30: 'Last 30 days', lastMonth: 'Last month',
};
export const SPAN_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 3, label: 'Quarter width' }, { value: 4, label: 'One third' }, { value: 6, label: 'Half width' },
  { value: 8, label: 'Two thirds' }, { value: 12, label: 'Full width' },
];

export interface LayoutFilters { range?: RangeKey; branchId?: string }
export interface LayoutWidget { id: string; span: number; filters?: LayoutFilters }
export interface DashboardLayout { version: 1; widgets: LayoutWidget[] }

export interface WidgetResult {
  id: string; span: number; title: string; category: string; view: string; definition: string;
  filters: LayoutFilters; status: 'ok' | 'error' | 'denied'; data?: Record<string, any>; error?: string;
}

export interface Capabilities { customize: boolean; createTemplates: boolean; manageTemplates: boolean; manageAssignments: boolean }

export interface MyDashboard {
  source: 'PERSONAL' | 'USER_TEMPLATE' | 'ROLE_TEMPLATE' | 'SYSTEM_DEFAULT';
  templateId: string | null; templateName: string | null; hasPersonalLayout: boolean;
  capabilities: Capabilities; layout: DashboardLayout; widgets: WidgetResult[];
}

export interface CatalogWidget {
  id: string; title: string; description: string; category: string; view: string; definition: string;
  defaultSpan: number; defaultRange: RangeKey | null; ranges: RangeKey[]; branchFilter: boolean;
}
export interface Catalog { widgets: CatalogWidget[]; branches: Array<{ id: string; name: string }>; capabilities: Capabilities }

export interface TemplateDto {
  id: string; name: string; description: string | null; isActive: boolean; assignmentCount: number; updatedAt: string;
  layout: DashboardLayout; widgets: Array<LayoutWidget & { title: string; viewable: boolean }>;
}
export interface AssignmentDto {
  id: string; template: { id: string; name: string; isActive: boolean };
  target: { type: 'ROLE' | 'USER'; id: string; name: string; email?: string };
}
export interface AssignmentTargets { roles: Array<{ id: string; name: string }>; members: Array<{ id: string; name: string; email: string }> }

export const dashboardApi = {
  mine: () => apiGet<MyDashboard>('/dashboard'),
  catalog: () => apiGet<Catalog>('/dashboard/catalog'),
  previewData: (widgets: LayoutWidget[]) => apiPost<{ widgets: WidgetResult[] }>('/dashboard/widgets/data', { widgets }),
  saveLayout: (widgets: LayoutWidget[]) => apiPut<DashboardLayout>('/dashboard/layout', { layout: { widgets } }),
  resetLayout: () => apiDelete<unknown>('/dashboard/layout'),
  templates: () => apiGet<TemplateDto[]>('/dashboard/templates'),
  createTemplate: (b: { name: string; description?: string; widgets: LayoutWidget[] }) =>
    apiPost<TemplateDto>('/dashboard/templates', { name: b.name, description: b.description, layout: { widgets: b.widgets } }),
  updateTemplate: (id: string, b: { name?: string; description?: string; isActive?: boolean; widgets?: LayoutWidget[] }) =>
    apiPut<TemplateDto>(`/dashboard/templates/${id}`, { name: b.name, description: b.description, isActive: b.isActive, ...(b.widgets && { layout: { widgets: b.widgets } }) }),
  deleteTemplate: (id: string) => apiDelete<unknown>(`/dashboard/templates/${id}`),
  assignments: () => apiGet<AssignmentDto[]>('/dashboard/assignments'),
  targets: () => apiGet<AssignmentTargets>('/dashboard/assignment-targets'),
  assign: (b: { templateId: string; roleId?: string; companyUserId?: string }) => apiPut<unknown>('/dashboard/assignments', b),
  unassign: (id: string) => apiDelete<unknown>(`/dashboard/assignments/${id}`),
};

/** Server-sent message from a failed call, for a snackbar. */
export function errorMessage(e: unknown): string {
  const anyE = e as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return anyE?.response?.data?.error ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}
